import {  useState  } from 'react';
import { Timer, Play, Pause, RotateCcw } from 'lucide-react';
import { formatTime } from '../../utils/time';
import { DURATION_OPTIONS } from '../../utils/constants';

interface GameTimerProps {
  time: number;
  isRunning: boolean;
  remainingTime: number | null;
  onToggle: () => void;
  onReset: () => void;
  onSetDuration: (duration: number) => void;
}

export const GameTimer: React.FC<GameTimerProps> = ({
  time,
  isRunning,
  remainingTime,
  onToggle,
  onReset,
  onSetDuration,
}) => {
  const [showDurationSelector, setShowDurationSelector] = useState(false);

  return (
    <div className="flex flex-col items-center gap-4 mb-8">
      <div className="flex items-center justify-center gap-4 bg-gradient-to-r from-indigo-50 to-purple-50 dark:from-indigo-900/20 dark:to-purple-900/20 p-4 rounded-xl w-full border border-indigo-100 dark:border-indigo-800 transition-colors duration-200">
        <div className="flex items-center gap-2">
          <Timer className="w-5 h-5 text-indigo-600 dark:text-indigo-400" />
          <span className="text-xl font-mono font-bold text-gray-800 dark:text-gray-200">
            {remainingTime !== null ? formatTime(remainingTime) : formatTime(time)}
          </span>
        </div>
        
        <div className="flex gap-2">
          <button
            onClick={onToggle}
            className="p-2 rounded-lg bg-indigo-100 dark:bg-indigo-800 hover:bg-indigo-200 dark:hover:bg-indigo-700 transition-colors text-gray-700 dark:text-gray-200"
            aria-label={isRunning ? 'Pause timer' : 'Start timer'}
          >
            {isRunning ? <Pause className="w-4 h-4" /> : <Play className="w-4 h-4" />}
          </button>
          
          <button
            onClick={onReset}
            className="p-2 rounded-lg bg-indigo-100 dark:bg-indigo-800 hover:bg-indigo-200 dark:hover:bg-indigo-700 transition-colors text-gray-700 dark:text-gray-200"
            aria-label="Reset timer"
          >
            <RotateCcw className="w-4 h-4" />
          </button>
        </div>
      </div>

      <div className="flex flex-col items-center gap-2">
        <button
          onClick={() => setShowDurationSelector(!showDurationSelector)}
          className="text-sm text-indigo-600 dark:text-indigo-400 hover:text-indigo-700 dark:hover:text-indigo-300 hover:underline font-medium"
        >
          {showDurationSelector ? 'Hide duration options' : 'Set timer duration'}
        </button>

        {showDurationSelector && (
          <div className="flex flex-wrap gap-2 justify-center">
            {DURATION_OPTIONS.map((duration) => (
              <button
                key={duration}
                onClick={() => {
                  onSetDuration(duration);
                  setShowDurationSelector(false);
                }}
                className="px-3 py-1 text-sm rounded-full bg-indigo-100 dark:bg-indigo-800 hover:bg-indigo-200 dark:hover:bg-indigo-700 transition-colors text-gray-700 dark:text-gray-200 font-medium"
              >
                {duration} min
              </button>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};